import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { VariableSizeList as List, ListChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { ContextMenuTrigger } from "react-contextmenu";

import FieldListEntry from "./FieldListEntry";
import TreeItem from "../tree/TreeItem";
import { computeVisibleChildIds } from "./FieldList";
import { fieldMatchesSearchString } from "../../reducers/fields";
import { setSelectedFields, requestLazyChildFields, requestLazyFieldTree } from "../../actions/fieldTreeActions";
import highlightForSearch from "../../helpers/highlightForSearch";
import { getTreeSearch } from "../../reducers/search";
import { FieldVisibilityTypes } from "../../helpers/constants";
import { IAppState } from "../../types/stores/index";
import { IField } from "../../types/stores/fieldTypes";
import { IFieldsStore } from "../../types/stores/fields";

// Row heights (px). The old tree's .tree-view_item is 30px tall with 5px top/bottom
// padding (content-box) = ~40px per row;
const SINGLE_LINE_HEIGHT = 40;
// Matches $tree-indent-amount (.tree-view_children margin-left) in _tree-view.styl.
const INDENT_PX = 20;

type Props = {
    TreeType: number;
    editable: boolean;
    showHiddenFields: boolean;
    isPIIOnly: boolean;
    ctrlShift: boolean;
    isModal: boolean;
    showTabs: boolean;
    tabType: number;
    // When true, folder children are fetched on demand as folders are expanded
    // (lazy-loading-fieldtree feature). Only meaningful while not searching.
    lazy?: boolean;
};

interface IVisibleRow {
    workid: string | number;
    fieldkey: number;
    depth: number;
    isLeaf: boolean;
}

interface IRowData {
    rows: Array<IVisibleRow>;
    byFolder: { [workId: string]: IField };
    fields: IFieldsStore;
    treeSearch: string | Array<string>;
    altTreeSearched: boolean;
    pathname: string | null | undefined;
    loadingFolders: { [key: string]: boolean };
    byParent: { [key: string]: Array<string | number> };
    expanded: Set<string | number>;
    toggle: (workid: string | number) => void;
    onCtrlShift: (lastSelected: number, fieldKey: number) => void;
    props: Props;
}

const Row: React.FC<ListChildComponentProps<IRowData>> = ({ index, style, data }) => {
    const {
        rows,
        byFolder,
        treeSearch,
        altTreeSearched,
        pathname,
        loadingFolders,
        byParent,
        expanded,
        toggle,
        onCtrlShift,
        props,
    } = data;
    const row = rows[index];
    const field = byFolder[row.workid];
    if (!field) {
        return null;
    }

    const indentStyle: React.CSSProperties = {
        ...style,
        overflow: "hidden",
        boxSizing: "border-box",
    };

    if (!row.isLeaf) {
        const isCollapsed = !expanded.has(row.workid);
        const useMaterialIcons =
            !!pathname &&
            (pathname.includes("flows") ||
                pathname.includes("schedules") ||
                pathname.includes("layouts") ||
                pathname.includes("/admin/fields/edit") ||
                pathname.includes("/admin/restricted-fields"));
        let folderIcon;
        if (useMaterialIcons) {
            folderIcon = (
                <i className="material-icons" style={{ position: "relative", fontSize: "2em" }}>
                    {isCollapsed ? "folder" : "folder_open"}
                </i>
            );
        } else {
            folderIcon = (
                <i
                    className={
                        isCollapsed ? "proton-icon proton-icon-folder-closed" : "proton-icon proton-icon-folder-open"
                    }
                />
            );
        }
        const header = (
            <TreeItem staticClasses="tree-view_header" handleClick={() => toggle(row.workid)}>
                {folderIcon}

                <span className="tree-header-label">
                    {" "}
                    {loadingFolders[row.workid] ? (
                        <i className="fa fa-spinner fa-spin" style={{ marginBottom: 5 }} />
                    ) : null}
                    {highlightForSearch(field.text, treeSearch)}
                </span>
            </TreeItem>
        );

        // Match the non-virtualized tree: folders are wrapped in the "edit-context"
        // trigger so the custom right-click menu (FieldEditContextMenu) works.
        const hasChildren = Boolean(byParent[row.workid] && byParent[row.workid].length > 0);

        return (
            <div style={indentStyle}>
                <div style={{ marginLeft: row.depth * INDENT_PX }}>
                    <ContextMenuTrigger
                        id="edit-context"
                        collect={triggerProps => triggerProps}
                        holdToDisplay={-1}
                        // @ts-ignore
                        item={field}
                        // @ts-ignore
                        hasChildren={hasChildren}
                    >
                        {header}
                    </ContextMenuTrigger>
                </div>
            </div>
        );
    }

    return (
        <div style={indentStyle}>
            <div style={{ marginLeft: row.depth * INDENT_PX }}>
                <FieldListEntry
                    fieldKey={field.fieldkey}
                    fields={data.fields}
                    altTreeSearched={altTreeSearched}
                    TreeType={props.TreeType}
                    ctrlShift={props.ctrlShift}
                    onCtrlShift={onCtrlShift}
                    editable={props.editable}
                    showHiddenFields={props.showHiddenFields}
                    isGrayout={false}
                    tabType={props.tabType}
                    showTabs={props.showTabs}
                    isTableActive={field.TableActive === "Y"}
                />
            </div>
        </div>
    );
};

const VirtualizedFieldTree: React.FC<Props> = props => {
    const dispatch = useDispatch();
    const listRef = useRef<List>(null);

    const fields = useSelector((state: IAppState) => {
        if (props.TreeType == FieldVisibilityTypes.EXPORT) {
            return state.layoutfields;
        }
        if (props.TreeType == FieldVisibilityTypes.REPORT) {
            return state.reportFields;
        }
        return state.fields;
    });

    const searchResults = useSelector((state: IAppState) => state.vars.searchResults) as {
        [workId: string]: IField;
    };
    const searchResultsByParent = useSelector((state: IAppState) => state.vars.searchResultsByParent) as {
        [key: string]: Array<string | number>;
    };
    const booleanSearchResultsReceived = useSelector(
        (state: IAppState) => state.vars.booleanSearchResultsReceived === true
    );
    const treeSearch = useSelector((state: IAppState) => getTreeSearch(state));
    const rawTreeSearch = useSelector((state: IAppState) => state.search.treeSearch);
    const selectedFields = useSelector((state: IAppState) => state.selected.selectedFields);
    const pathname = useSelector(
        (state: IAppState) => state.router && state.router.location && state.router.location.pathname
    );

    // Inputs required by computeVisibleChildIds (mirrors FieldList's selector)
    const fieldVisibilityFilter = useSelector((state: IAppState) => state.adminDesigner.fieldVisibilityFilter);
    const enabledFieldVisibilityTypes = useSelector(
        (state: IAppState) => state.fieldsByCompany.enabledFieldVisibilityTypes
    );
    const selectedFieldLabels = useSelector((state: IAppState) => state.search.selectedFieldLabels);
    const selectedFieldLabels3 = useSelector((state: IAppState) => state.search.selectedFieldLabels3);
    const fieldLabels = useSelector((state: IAppState) => state.fieldLabels.labels);
    const fieldLabels3 = useSelector((state: IAppState) => state.fieldLabels3.labels);
    const fieldClassificationTables = useSelector((state: IAppState) => state.session.fieldClassificationTables);
    const loadedFolders = useSelector((state: IAppState) => state.vars?.loadedFolders ?? {});
    const loadingFolders = useSelector((state: IAppState) => state.vars?.loadingFolders ?? {});

    // When a server search is active, walk the pre-filtered search result structure;
    // otherwise walk the normal field tree.
    const byFolder = booleanSearchResultsReceived ? searchResults : fields.byFolder;
    const byParent = booleanSearchResultsReceived ? searchResultsByParent : fields.byParent;

    const searching = Boolean(rawTreeSearch) || booleanSearchResultsReceived;

    // Folders auto-expand during a search (so matches are visible) and default collapsed
    // otherwise. Manual toggles are preserved while the search state is unchanged.
    const [expanded, setExpanded] = useState<Set<string | number>>(new Set());
    const expandedRef = useRef(expanded);
    expandedRef.current = expanded;

    // In lazy mode, make sure the root folders are loaded (children load on expand).
    const rootRequestedRef = useRef(false);
    useEffect(() => {
        if (props.lazy && !loadedFolders["#"] && !rootRequestedRef.current) {
            rootRequestedRef.current = true;
            dispatch(requestLazyFieldTree());
        }
    }, [props.lazy, loadedFolders, dispatch]);

    const searchActive = Boolean(rawTreeSearch) || booleanSearchResultsReceived;
    const wasSearchActiveRef = useRef(searchActive);
    const autoExpandedIdsRef = useRef<Set<string | number>>(new Set());

    useEffect(() => {
        const wasSearching = wasSearchActiveRef.current;
        wasSearchActiveRef.current = searchActive;

        if (searchActive) {
            // Union in any folders present in byFolder (search matches / their ancestors),
            // without clobbering folders the user has manually collapsed, and without
            // requiring the whole search-result set to have arrived in one shot (lazy mode /
            // Exact search stream data in over multiple merges).
            const folderIds = Object.keys(byFolder).filter(id => byFolder[id] && !byFolder[id].isLeaf);
            const newIds = folderIds.filter(id => !autoExpandedIdsRef.current.has(id));
            newIds.forEach(id => autoExpandedIdsRef.current.add(id));

            if (newIds.length > 0) {
                setExpanded(prev => {
                    const next = new Set(prev);
                    newIds.forEach(id => next.add(id));
                    return next;
                });
            }
        } else if (wasSearching) {
            // Just left search state (cleared the search) - collapse back to default.
            setExpanded(new Set());
            autoExpandedIdsRef.current = new Set();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchActive, byFolder]);

    const toggle = useCallback(
        (workid: string | number) => {
            const willExpand = !expandedRef.current.has(workid);
            setExpanded(prev => {
                const next = new Set(prev);
                if (willExpand) {
                    next.add(workid);
                } else {
                    next.delete(workid);
                }
                return next;
            });
            // Lazy mode: fetch the folder's children the first time it is expanded.
            // requestLazyChildFields no-ops if they are already loaded.
            if (props.lazy && willExpand) {
                dispatch(requestLazyChildFields(workid, undefined, false));
            }
        },
        [props.lazy, dispatch]
    );

    const rows = useMemo(() => {
        if (!byParent || !byFolder) {
            return [] as Array<IVisibleRow>;
        }

        const clientSearch = Boolean(rawTreeSearch) && !booleanSearchResultsReceived;
        const out: Array<IVisibleRow> = [];
        const containsCache = new Map<string | number, boolean>();

        const folderContainsMatch = (folderId: string | number): boolean => {
            if (containsCache.has(folderId)) {
                return containsCache.get(folderId)!;
            }
            containsCache.set(folderId, false); // guard against cycles
            const kids = byParent[folderId] || [];
            let result = false;
            for (const kid of kids) {
                const f = byFolder[kid];
                if (!f) {
                    continue;
                }
                if (f.isLeaf) {
                    if (fieldMatchesSearchString(f, treeSearch)) {
                        result = true;
                        break;
                    }
                } else if (fieldMatchesSearchString(f, treeSearch) || folderContainsMatch(kid)) {
                    result = true;
                    break;
                }
            }
            containsCache.set(folderId, result);
            return result;
        };

        const walk = (parentKey: string | number, depth: number, showAll: boolean) => {
            const childIds = byParent[parentKey];
            const { folderIds, fieldIds } = computeVisibleChildIds({
                fieldsById: byFolder as { [workId: number]: IField },
                selectedFieldId: parentKey,
                childIds: childIds || [],
                treeSearch,
                showAllDuringSearch: showAll,
                isPIIOnly: props.isPIIOnly,
                editable: props.editable,
                fieldVisibilityFilter,
                enabledFieldVisibilityTypes,
                fieldLabelIds: selectedFieldLabels,
                fieldLabelIds3: selectedFieldLabels3,
                fieldLabels,
                fieldLabels3,
                fieldClassificationTables,
                searchResults: searchResults as { [workId: number]: IField },
                booleanSearchResultsReceived,
                showTabs: props.showTabs,
                tabType: props.tabType,
            });

            // Render folders first, then leaf fields (matching FieldList order)
            for (const folderId of folderIds) {
                const folder = byFolder[folderId];
                if (!folder) {
                    continue;
                }
                const folderMatches = clientSearch ? fieldMatchesSearchString(folder, treeSearch) : false;
                const include = !clientSearch || showAll || folderMatches || folderContainsMatch(folderId);
                if (!include) {
                    continue;
                }
                out.push({ workid: folderId, fieldkey: folder.fieldkey, depth, isLeaf: false });
                if (expanded.has(folderId)) {
                    walk(folderId, depth + 1, showAll || folderMatches);
                }
            }
            for (const fieldId of fieldIds) {
                const f = byFolder[fieldId];
                if (!f) {
                    continue;
                }
                out.push({ workid: fieldId, fieldkey: f.fieldkey, depth, isLeaf: true });
            }
        };

        walk("#", 0, false);
        return out;
    }, [
        byParent,
        byFolder,
        rawTreeSearch,
        treeSearch,
        booleanSearchResultsReceived,
        expanded,
        searchResults,
        fieldVisibilityFilter,
        enabledFieldVisibilityTypes,
        selectedFieldLabels,
        selectedFieldLabels3,
        fieldLabels,
        fieldLabels3,
        fieldClassificationTables,
        props.isPIIOnly,
        props.editable,
        props.showTabs,
        props.tabType,
    ]);

    // VariableSizeList caches row heights; reset when the row set changes.
    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0, true);
        }
    }, [rows]);

    const getItemSize = useCallback(() => SINGLE_LINE_HEIGHT, []);

    const onCtrlShift = useCallback(
        (lastSelected: number, newSelected: number) => {
            const leafFieldKeys = rows.filter(r => r.isLeaf).map(r => r.fieldkey);
            const indexA = leafFieldKeys.indexOf(lastSelected);
            const indexB = leafFieldKeys.indexOf(newSelected);
            if (indexA === -1 || indexB === -1) {
                return;
            }
            const start = Math.min(indexA, indexB);
            const end = Math.max(indexA, indexB);
            for (let i = start; i <= end; i++) {
                const fieldKey = leafFieldKeys[i];
                if (!selectedFields.includes(fieldKey)) {
                    dispatch(setSelectedFields(fieldKey));
                }
            }
        },
        [rows, selectedFields, dispatch]
    );

    if (rows.length === 0) {
        return <span>{searching ? "(empty)" : ""}</span>;
    }

    const itemData: IRowData = {
        rows,
        byFolder,
        fields,
        treeSearch,
        altTreeSearched: booleanSearchResultsReceived,
        pathname,
        loadingFolders,
        byParent,
        expanded,
        toggle,
        onCtrlShift,
        props,
    };

    return (
        <div style={{ height: "66vh", minHeight: 300 }}>
            <AutoSizer>
                {({ height, width }) => (
                    <List
                        ref={listRef}
                        height={height}
                        width={width}
                        itemCount={rows.length}
                        itemSize={getItemSize}
                        itemData={itemData}
                        overscanCount={8}
                    >
                        {Row}
                    </List>
                )}
            </AutoSizer>
        </div>
    );
};

export default VirtualizedFieldTree;
